<template>
    <div class="relative" ref="dropdownContainer">
        <button @click="toggleDropdown" class="flex items-center justify-center ml-8 sm:ml-20 group">
            <span
                class="relative z-10 flex items-center justify-center w-32 h-32 bg-yellow-200 rounded-full group-hover:bg-yellow-250 self-animate group-[.active]:bg-yellow-250"
            >
                <span class="flex-shrink-0 block w-24 h-24 fill-current text-primary">
                    <IconPersonSVG />
                </span>
            </span>

            <span
                class="flex items-center h-32 pl-24 pr-16 -ml-16 bg-yellow-100 group-hover:bg-yellow-200 group-[.active]:bg-yellow-200 rounded-r-3xl self-animate"
            >
                <span
                    class="mr-8 overflow-hidden font-semibold leading-none 2xs-down:truncate 2xs-down:max-w-64 text-2xs text-primary 2xs:block"
                    >{{ $te(selectedOrganization) ? $t(selectedOrganization) : selectedOrganization }}</span
                >

                <span class="flex-shrink-0 block w-10 h-10 -rotate-90 fill-current text-primary">
                    <IconArrowBackSVG />
                </span>
            </span>
        </button>

        <div
            v-show="showDropdown"
            class="absolute right-0 top-full mt-10 min-w-[320px] xs:min-w-[375px] z-10 p-24 text-black bg-white rounded-lg self-window shadow-card"
        >
            <button
                class="absolute flex items-center justify-center flex-shrink-0 w-32 h-32 bg-blue-100 rounded-full self-animate top-16 right-16 hover:bg-blue-200"
            >
                <span @click="toggleDropdown" class="flex-shrink-0 block w-20 h-20 text-black fill-current">
                    <IconXSVG />
                </span>
            </button>

            <div v-if="user" class="flex items-end justify-between">
                <div>
                    <span class="block font-semibold text-black text-md">{{ user }}</span>
                    <span v-if="isPlatinumCardOwner" class="block mt-4 text-base text-black">{{ $t('account_login.plat_member') }}</span>
                    <span v-else-if="isGoldCardOwner" class="block mt-4 text-base text-black">{{ $t('account_login.gold_member') }}</span>
                    <span v-if="isGoldCardOwner || isPlatinumCardOwner" class="block mt-4 text-base text-black"
                        >{{ customerInfo.bp_collected }} {{ $t('account_login.bonuspoints') }}</span
                    >
                </div>
                <div>
                    <img
                        v-if="isPlatinumCardOwner"
                        class="w-[82px] h-auto block rounded-base shadow-card-sm"
                        src="../assets/static/images/platinum.png"
                        alt="Logo"
                    />
                    <img
                        v-else-if="isGoldCardOwner"
                        class="w-[82px] h-auto block rounded-base shadow-card-sm"
                        src="../assets/static/images/kuldkaart-alt.png"
                        alt="Logo"
                    />
                </div>
            </div>
            <a @click="toggleDropdown">
                <RouterLink class="mt-24 self-btn-primary" :to="`/${$i18n.locale}/dashboard`">
                    {{ $t('account_login.dashboard') }}
                    <span class="text-yellow-300 self-btn-primary__icon">
                        <IconArrowAltSVG />
                    </span>
                </RouterLink>
            </a>

            <!--            <a @click="toggleDropdown">-->
            <!--                <router-link :to="`/${$i18n.locale}/favourites`" href="#" class="mt-24 self-btn-plain group">-->
            <!--                    <span class="flex-shrink-0 block w-24 h-24 mr-4 text-primary group-hover:text-blue-300 self-animate">-->
            <!--                        <IconHeartSVG/>-->
            <!--                    </span>-->
            <!--                    <span class="self-btn-plain__text">{{ $t('my_favorites') }}</span>-->
            <!--                </router-link>-->
            <!--            </a>-->
            <span v-if="organizations.length !== 0" class="block mt-24 text-base text-black">{{ $t('account_login.choose_role') }}</span>
            <button
                v-for="org in organizations"
                :value="org.customerId"
                :key="org.customerId"
                @click="selectOrganization(org)"
                class="mt-8 self-btn-role"
                :class="{ active: org.name === selectedOrganization }"
            >
                <span class="self-btn-role__text">{{ $te(org.name) ? $t(org.name) : org.name }}</span>
                <span class="self-btn-role__icon">
                    <IconCheckedSVG />
                </span>
            </button>
            <a href="#" @click="logoutOnClick" class="mt-24 self-btn-plain">
                <span class="self-btn-plain__text">{{ $t('nav.top.logout') }}</span>
            </a>
        </div>
    </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import IconPersonSVG from '../assets/static/icons-src/icon-person.svg';
import IconArrowBackSVG from '../assets/static/icons-src/icon-arrow-back.svg';
import IconXSVG from '../assets/static/icons-src/icon-x.svg';
import IconArrowAltSVG from '../assets/static/icons-src/icon-arrow-alt.svg';
import IconHeartSVG from '../assets/static/icons-src/icon-heart.svg';
import IconCheckedSVG from '../assets/static/icons-src/icon-checked.svg';
import store from '@/store';
import { IAuthorizers } from '@/domain/IAuthorizers';
import { ICustomer } from '@/domain/ICustomer';
import i18n from '@/i18n';

@Component({
    components: {
        IconPersonSVG,
        IconArrowBackSVG,
        IconXSVG,
        IconArrowAltSVG,
        IconHeartSVG,
        IconCheckedSVG
    }
})
export default class AccountLogin extends Vue {
    showDropdown = false;

    get user(): string {
        if (store.state.token && store.state.customerInfo && store.state.customerType === 'P') {
            return store.state.customerName;
        }
        return null;
    }

    get customerInfo(): ICustomer {
        return this.$store.getters.getCustomerInfo;
    }

    get isPlatinumCardOwner(): boolean {
        if (this.customerInfo.customer_cards) {
            return this.customerInfo.customer_cards.find((card) => card.card_type_name === 'Platinum Club') !== undefined;
        }
        return false;
    }

    get isGoldCardOwner(): boolean {
        if (this.customerInfo.customer_cards) {
            return this.customerInfo.customer_cards.find((card) => card.card_type_name === 'Kuldkaart') !== undefined;
        }
        return false;
    }

    get organizations(): IAuthorizers[] {
        if (store.state.token && store.state.customerInfo) {
            return this.$store.getters.getAuthorizers;
        }
        return [];
    }

    get selectedOrganization(): string {
        const selectedOrg = this.organizations.find((org) => org.customerId === this.selectedCustomerId);
        return selectedOrg ? selectedOrg.name : '';
    }

    get selectedCustomerId(): number {
        return store.getters.getCustomerId;
    }

    set selectedCustomerId(value: number) {
        store.commit('setCustomerId', value);
    }

    async logoutOnClick() {
        await store.dispatch('clear');
        // eslint-disable-next-line
        this.$router.push(`/${this.$i18n.locale}/account/login`).catch(() => {});
    }

    toggleDropdown(event) {
        this.showDropdown = !this.showDropdown;
        if (this.showDropdown) {
            // Add a click event listener to close the dropdown when clicking outside
            document.addEventListener('click', this.closeDropdownOnClickOutside);
        } else {
            // Remove the event listener when the dropdown is closed
            document.removeEventListener('click', this.closeDropdownOnClickOutside);
        }
    }

    closeDropdownOnClickOutside(event) {
        // Check if the click event target is outside the dropdown container
        if (this.$refs.dropdownContainer && !(this.$refs.dropdownContainer as Element).contains(event.target)) {
            this.showDropdown = false;
            document.removeEventListener('click', this.closeDropdownOnClickOutside);
        }
    }

    async selectOrganization(org) {
        this.selectedCustomerId = org.customerId;
        this.showDropdown = false;
        const res = await store.dispatch('authenticateUser');
        // If role change fails go back to personal account role
        if (!res) {
            this.selectedCustomerId = this.organizations.find(
                (organization) => organization.name === i18n.t('nav.top.personal_acc')
            ).customerId;
            await store.dispatch('authenticateUser');
        }
        const currentRoute = this.$route.path;
        const destinationRoute = `/${this.$i18n.locale}/dashboard`;
        if (currentRoute !== destinationRoute) {
            await this.$router.push(destinationRoute);
        }
    }
}
</script>

<style scoped></style>
